import { ChevronLeft, ChevronRight, Layers, Users, Cloud, Settings } from 'lucide-react';
import { useState } from 'react';
import { LayerPanel } from '@/components/Sidebar/LayerPanel';
import { UsersPanel } from '@/components/Sidebar/UsersPanel';
import { WeatherPanel } from '@/components/Sidebar/WeatherPanel';
import { ThemeSelector } from '@/components/Settings/ThemeSelector';
import { cn } from '@/lib/utils';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

type Tab = 'layers' | 'users' | 'weather' | 'settings';

const tabs: { id: Tab; icon: React.ElementType; label: string }[] = [
  { id: 'layers', icon: Layers, label: 'Layers' },
  { id: 'users', icon: Users, label: 'Users' },
  { id: 'weather', icon: Cloud, label: 'Weather' },
  { id: 'settings', icon: Settings, label: 'Settings' },
];

export function Sidebar({ isOpen, onToggle }: SidebarProps) {
  const [activeTab, setActiveTab] = useState<Tab>('layers');

  return (
    <aside
      className={cn(
        'relative flex flex-col border-r border-border bg-card transition-all duration-200',
        isOpen ? 'w-72' : 'w-0'
      )}
    >
      {/* Toggle — placed fully outside the sidebar edge */}
      <button
        onClick={onToggle}
        className="absolute -right-6 top-3 z-10 w-6 h-8 rounded-r-md flex items-center justify-center bg-card border border-border border-l-0 text-muted-foreground hover:text-foreground transition-colors"
        aria-label={isOpen ? 'Close sidebar' : 'Open sidebar'}
      >
        {isOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
      </button>

      {isOpen && (
        <>
          {/* Tab bar */}
          <div className="flex border-b border-border">
            {tabs.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={cn(
                  'flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium transition-colors border-b-2',
                  activeTab === id
                    ? 'text-foreground border-primary'
                    : 'text-muted-foreground border-transparent hover:text-foreground'
                )}
                title={label}
              >
                <Icon size={13} />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'layers' && <LayerPanel />}
            {activeTab === 'users' && <UsersPanel />}
            {activeTab === 'weather' && <WeatherPanel />}
            {activeTab === 'settings' && (
              <div className="p-3">
                <ThemeSelector />
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  );
}
